import React, { useRef, useCallback, useState } from 'react';
import { Card, Table, Button, Form, Row, Col } from 'react-bootstrap';

const RegistrationSlip = React.forwardRef(({ student, schoolInfo, feeData }, ref) => {
  const printRef = useRef();
  const [orientation, setOrientation] = useState('portrait');

  // Public folder image paths
  const imageUrls = {
    nielitLogo: '/images/nielit.jpeg',
    schoolLogo: '/images/gsk.jpg',
    studentPhoto: student?.photoURL || '/images/student-placeholder.png'
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN');
  };

  const handlePrint = useCallback(() => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank', 'width=1000,height=600');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Registration Slip - ${student.studentName}</title>
          <link
            href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
            rel="stylesheet"
          >
          <style>
            @media print {
              body {
                margin: 0;
                padding: 0;
                background: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .print-hide {
                display: none !important;
              }
              .dual-copy-container {
                display: flex;
                width: 100%;
                gap: 20px;
                margin: 0;
                padding: 0;
              }
              .copy-section {
                flex: 1;
                border: 2px solid #000;
                page-break-inside: avoid;
              }
              .registration-slip {
                margin: 0;
                padding: 15px;
                background: white;
                font-family: 'Arial', sans-serif;
                height: 100%;
              }
              .table th {
                background-color: #f8f9fa !important;
                border: 1px solid #dee2e6;
              }
              .table td {
                border: 1px solid #dee2e6;
              }
              @page {
                margin: 10mm;
                size: A4;
              }
            }
            .dual-copy-container {
              display: flex;
              width: 297mm;
              gap: 20px;
              margin: 0 auto;
              padding: 10mm;
            }
            .copy-section {
              flex: 1;
              border: 2px solid #000;
              background: white;
            }
            .registration-slip {
              padding: 15px;
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
              position: relative;
              overflow: hidden;
              height: 100%;
            }
            .header-section {
              border-bottom: 3px double #333;
              padding-bottom: 15px;
              margin-bottom: 20px;
              position: relative;
            }
            .watermark {
              position: absolute;
              opacity: 0.05;
              font-size: 80px;
              font-weight: bold;
              transform: rotate(-45deg);
              top: 30%;
              left: 10%;
              z-index: 0;
              color: #000;
              pointer-events: none;
            }
            .official-stamp {
              position: absolute;
              right: 20px;
              top: 20px;
              border: 2px dashed #333;
              padding: 10px;
              border-radius: 50%;
              transform: rotate(15deg);
              opacity: 0.8;
            }
            .fee-table {
              font-size: 0.85em;
            }
            .signature-section {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 2px dashed #333;
            }
            .copy-badge {
              position: absolute;
              top: 10px;
              right: 10px;
              color: white;
              padding: 5px 10px;
              border-radius: 5px;
              font-size: 0.8em;
              font-weight: bold;
              z-index: 2;
            }
          </style>
        </head>
        <body>
          <div>${printContent.innerHTML}</div>
          <script>
            window.onload = function() {
              window.focus();
              setTimeout(() => {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  }, [student.studentName]);

  const SlipCopy = ({ type }) => (
    <div className="copy-section">
      <Card className="registration-slip position-relative h-100">
        {/* Watermark */}
        <div className="watermark">{type} COPY</div>
        
        {/* Official Stamp Area */}
        <div className="official-stamp text-center">
          <div style={{ fontSize: '10px', fontWeight: 'bold' }}>
            OFFICIAL STAMP
          </div>
        </div>

        {/* Copy Badge */}
        <div className={`copy-badge ${type === 'STUDENT' ? 'bg-success' : 'bg-primary'}`}>
          {type} COPY
        </div>

        {/* Header with Logos */}
        <Card.Header className={`${type === 'STUDENT' ? 'bg-success' : 'bg-primary'} text-white py-2 header-section`}>
          <div className="d-flex align-items-center justify-content-between">
            {/* Left Logo - NIELIT */}
            <div className="text-center">
              <div 
                style={{ 
                  width: '70px', 
                  height: '70px', 
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '3px',
                  border: '2px solid #fff',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={imageUrls.nielitLogo} 
                  alt="NIELIT Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.nielit-fallback');
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div 
                  className="nielit-fallback"
                  style={{ 
                    display: 'none',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    padding: '2px'
                  }}
                >
                  <strong style={{ fontSize: '11px', color: '#000' }}>NIELIT</strong>
                  <small style={{ fontSize: '7px', color: '#000' }}>GOVT. OF INDIA</small>
                </div>
              </div>
              <small className="text-white mt-1 d-block" style={{ fontSize: '10px' }}>Approved by NIELIT</small>
            </div>

            {/* School Info */}
            <div className="text-center flex-grow-1 mx-3">
              <h5 className="mb-1 fw-bold" style={{ fontSize: '18px' }}>{schoolInfo.name}</h5>
              <h6 className="mb-1" style={{ fontSize: '14px' }}>REGISTRATION SLIP</h6>
              <small style={{ fontSize: '11px' }}>{schoolInfo.address}</small>
              <br />
              <small style={{ fontSize: '10px' }}>Phone: {schoolInfo.phone} | Email: {schoolInfo.email}</small>
            </div>

            {/* Right Logo - School Logo */}
            <div className="text-center">
              <div 
                style={{ 
                  width: '70px', 
                  height: '70px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '3px',
                  border: '2px solid white',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={imageUrls.schoolLogo} 
                  alt="School Logo" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.school-fallback');
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div 
                  className="school-fallback"
                  style={{ 
                    display: 'none',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    padding: '2px'
                  }}
                >
                  <strong style={{ fontSize: '11px', color: '#000' }}>SCHOOL</strong>
                  <small style={{ fontSize: '7px', color: '#000' }}>LOGO</small>
                </div>
              </div>
              <small className="text-white mt-1 d-block" style={{ fontSize: '10px' }}>School Seal</small>
            </div>
          </div>
        </Card.Header>

        <Card.Body style={{ position: 'relative', zIndex: 1, padding: '10px' }}>
          {/* Student Information */}
          <div className="row mb-3">
            <div className="col-md-8">
              <Table bordered size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <th width="120" className="bg-light" style={{ fontSize: '12px' }}>Registration No.</th>
                    <td style={{ fontSize: '12px' }}><strong>{student.rollNumber}</strong></td>
                  </tr>
                  <tr>
                    <th className="bg-light" style={{ fontSize: '12px' }}>Student Name</th>
                    <td style={{ fontSize: '12px' }}><strong>{student.studentName}</strong></td>
                  </tr>
                  <tr>
                    <th className="bg-light" style={{ fontSize: '12px' }}>Parent's Name</th>
                    <td style={{ fontSize: '12px' }}>{student.parentName}</td>
                  </tr>
                  <tr>
                    <th className="bg-light" style={{ fontSize: '12px' }}>Class & Section</th>
                    <td style={{ fontSize: '12px' }}>{student.grade} - {student.section}</td>
                  </tr>
                  <tr>
                    <th className="bg-light" style={{ fontSize: '12px' }}>Date of Birth</th>
                    <td style={{ fontSize: '12px' }}>{formatDate(student.dateOfBirth)}</td>
                  </tr>
                  <tr>
                    <th className="bg-light" style={{ fontSize: '12px' }}>Contact Number</th>
                    <td style={{ fontSize: '12px' }}>{student.phone}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
            
            {/* Photo and Registration Date */}
            <div className="col-md-4 text-center">
              <div className="border p-1 mb-2 bg-white">
                <img
                  src={imageUrls.studentPhoto}
                  alt={student.studentName}
                  className="img-fluid"
                  style={{ 
                    width: '80px', 
                    height: '100px', 
                    objectFit: 'cover',
                    border: '1px solid #ddd'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div 
                  style={{ 
                    display: 'none',
                    width: '80px', 
                    height: '100px', 
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    textAlign: 'center'
                  }}
                >
                  Student<br />Photo
                </div>
              </div>
              <div className="border p-1 bg-light">
                <small className="text-muted" style={{ fontSize: '10px' }}>Registration Date</small><br/>
                <strong style={{ fontSize: '11px' }}>{formatDate(student.createdAt)}</strong>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="row mb-3">
            <div className="col-12">
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <th width="120" className="bg-light" style={{ fontSize: '12px' }}>Address</th>
                    <td style={{ fontSize: '12px' }}>{student.address}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          {/* Fee Details */}
          <div className="mt-3">
            <h6 className="border-bottom pb-1 mb-2 fw-bold" style={{ fontSize: '13px' }}>FEE DETAILS</h6>
            <Table bordered className="fee-table">
              <thead className="table-dark">
                <tr>
                  <th width="30" style={{ fontSize: '11px' }}>#</th>
                  <th style={{ fontSize: '11px' }}>Fee Type</th>
                  <th style={{ fontSize: '11px' }}>Amount (₹)</th>
                  <th style={{ fontSize: '11px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {feeData.map((fee, index) => (
                  <tr key={index}>
                    <td style={{ fontSize: '11px' }}>{index + 1}</td>
                    <td style={{ fontSize: '11px' }}>{fee.feeType}</td>
                    <td style={{ fontSize: '11px' }} className="text-end">₹{fee.amount.toLocaleString('en-IN')}</td>
                    <td style={{ fontSize: '11px' }}>
                      <span className={`badge ${fee.status === 'Paid' ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '10px' }}>
                        {fee.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="table-info fw-bold">
                  <td colSpan="2" className="text-end" style={{ fontSize: '12px' }}>TOTAL:</td>
                  <td className="text-end" style={{ fontSize: '12px' }}>
                    ₹{feeData.reduce((sum, fee) => sum + fee.amount, 0).toLocaleString('en-IN')}
                  </td>
                  <td style={{ fontSize: '12px' }}></td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Signature Section */}
          <div className="signature-section">
            <Row>
              <div className="col-4 text-center">
                <div className="border-bottom mb-1" style={{ height: '40px' }}></div>
                <div className="fw-bold" style={{ fontSize: '11px' }}>Student's Signature</div>
              </div>
              <div className="col-4 text-center">
                <div className="border-bottom mb-1" style={{ height: '40px' }}></div>
                <div className="fw-bold" style={{ fontSize: '11px' }}>Parent's Signature</div>
              </div>
              <div className="col-4 text-center">
                <div className="border-bottom mb-1" style={{ height: '40px' }}></div>
                <div className="fw-bold" style={{ fontSize: '11px' }}>Principal's Signature</div>
              </div>
            </Row>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  return (
    <div>
      {/* Print Controls */}
      <Card className="mb-3 print-hide">
        <Card.Header>
          <h6 className="mb-0">Print Registration Slip</h6>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Print Orientation</Form.Label>
                <Form.Select 
                  value={orientation} 
                  onChange={(e) => setOrientation(e.target.value)}
                  size="sm"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Both copies will be printed on single page
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6} className="text-end">
              <Button 
                variant="primary" 
                onClick={handlePrint}
                size="lg"
              >
                📄 Print Registration Slip
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Print Content - Both copies side by side */}
      <div ref={printRef} style={{ display: 'none' }}>
        <div className="dual-copy-container">
          {/* School Copy */}
          <SlipCopy type="SCHOOL" />
          
          {/* Student Copy */}
          <SlipCopy type="STUDENT" />
        </div>
      </div>

      {/* Preview */}
      <Card className="print-hide">
        <Card.Header>
          <h6 className="mb-0">Preview (Both copies will print on one page)</h6>
        </Card.Header>
        <Card.Body>
          <div className="dual-copy-container">
            {/* School Copy */}
            <SlipCopy type="SCHOOL" />
            
            {/* Student Copy */}
            <SlipCopy type="STUDENT" />
          </div>
        </Card.Body>
      </Card>
    </div>
  );
});

// Default props for the component
RegistrationSlip.defaultProps = {
  student: {
    rollNumber: 'REG2024001',
    studentName: 'John Doe',
    parentName: 'Robert Doe',
    grade: '10th',
    section: 'A',
    dateOfBirth: '2008-05-15',
    phone: '+91 9876543210',
    address: '123 Main Street, City, State - 123456',
    photoURL: '/images/student-placeholder.png',
    createdAt: new Date().toISOString()
  },
  schoolInfo: {
    name: 'DELHI PUBLIC SCHOOL',
    address: '123 Education Road, Knowledge City, Delhi - 110001',
    phone: '+91 11 23456789',
    email: 'info@dpsdelhi.com'
  },
  feeData: [
    {
      feeType: 'Admission Fee',
      amount: 5000,
      dueDate: '2024-04-01',
      status: 'Paid',
      paymentDate: '2024-03-28'
    },
    {
      feeType: 'Tuition Fee',
      amount: 25000,
      dueDate: '2024-04-05',
      status: 'Pending',
      paymentDate: null
    },
    {
      feeType: 'Transport Fee',
      amount: 12000,
      dueDate: '2024-04-10',
      status: 'Paid',
      paymentDate: '2024-03-30'
    },
    {
      feeType: 'Sports Fee',
      amount: 3000,
      dueDate: '2024-04-15',
      status: 'Pending',
      paymentDate: null
    }
  ]
};

export default RegistrationSlip;